import axios from 'axios';
import {
    Button,
    FormGroup,
    Label,
    Input,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import { useState, useEffect, useMemo } from 'react';
import { FaPencilAlt, FaArrowRight, FaTrash } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiURL1 } from '../../config';
import { useNavigate } from 'react-router-dom';

const TeacherAttendance = ({ filterDate, filterName, refreshAttendance, setRefreshAttendance }) => {
    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    const [teacherData, setTeacherData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 20;
    const [teacherColumns, setTeacherColumns] = useState([
        'Staff Name',
        'Email',
        'Date',
        'Check-in',
        'Check-out',
        'Hours Served (H)',
        'Total Working Hours (H)',
        'Total Break Time (H)',
        'Day Status',
        'Is Late',
        'Actions',
    ]);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editAttendanceData, setEditAttendanceData] = useState({
        checkInTime: '',
        checkOutTime: '',
        breaks: [],
    });
    const [selectedTeacherId, setSelectedTeacherId] = useState(null);
    const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);
    const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(null);
    const [teacherNames, setTeacherNames] = useState([]);
    const [breakDetailsModalOpen, setBreakDetailsModalOpen] = useState(false);
    const [selectedBreaks, setSelectedBreaks] = useState([]);

    const [addAttendanceData, setAddAttendanceData] = useState({
        teacherId: '',
        date: '',
        checkInTime: '',
        checkOutTime: '',
        breaks: [],
    });

    // New state for managing breaks in edit modal
    const [editBreaks, setEditBreaks] = useState([]);

    // Format date to 'yyyy-MM-dd' (local date)
    const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatTimeForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        let hours = d.getHours();
        let minutes = d.getMinutes();

        hours = String(hours).padStart(2, '0');
        minutes = String(minutes).padStart(2, '0');

        return `${hours}:${minutes}`;
    };

    const formatBreakTime = (breakTime) => {
        if (!breakTime) return '';
        const d = new Date(breakTime);
        let hours = d.getHours();
        let minutes = d.getMinutes();

        hours = String(hours).padStart(2, '0');
        minutes = String(minutes).padStart(2, '0');

        return `${hours}:${minutes}`;
    };

    const fetchTeacherData = async (page) => {
        setLoading(true);
        try {
            let url = `${apiURL1}/superadmin/getTeacherAttendance?page=${page}&limit=${itemsPerPage}`;
            const params = new URLSearchParams();
            if (filterName) params.append('name', filterName);
            if (filterDate) params.append('date', filterDate);

            const queryString = params.toString();
            if (queryString) {
                url += `&${queryString}`;
            }
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();

            if (data && data.data) {
                // console.log('Data.data is ', data.data)
                setTeacherData(data.data);
                setTotalPages(data.totalPages);
            } else {
                setTeacherData([]);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching teacher data:', error);
            toast.error('Error fetching teacher data!');
            setTeacherData([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const fetchTeacherNames = async () => {
        try {
            const response = await axios.get(`${apiURL1}/superadmin/getTeachersName`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            // console.log('response is ', response)

            // ✅ Extract only 'id' and 'name' from the response
            const formattedTeachers = response.data.teachers.map(teacher => ({
                _id: teacher._id,
                name: teacher.name
            }));

            // console.log('formattedTeachers is ', formattedTeachers)
            setTeacherNames(formattedTeachers);
        } catch (error) {
            // console.error('Error fetching teacher names:', error);
            toast.error('Error fetching teacher names!');
        }
    };

    useEffect(() => {
        fetchTeacherData(currentPage);
    }, [currentPage, filterName, filterDate]);

    useEffect(() => {
        if (refreshAttendance) {
            fetchTeacherData(currentPage);
            setRefreshAttendance(false); // Reset the refresh state
        }
    }, [refreshAttendance, currentPage, filterName, filterDate, setRefreshAttendance]);

    const calculateHoursServed = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return '-';
        const checkInTime = new Date(checkIn);
        const checkOutTime = new Date(checkOut);
        const diffInMs = Math.abs(checkOutTime - checkInTime);
        const diffInHours = diffInMs / (1000 * 60 * 60);
        return diffInHours.toFixed(2);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxPagesToShow = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    }, [totalPages, currentPage]);

    const [teacherTypes, setTeacherTypes] = useState({});

    const fetchTeacherType = async (teacherId) => {
        try {
            const response = await fetch(`${apiURL1}/superadmin/getTeacherById/${teacherId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            return data.teacher.type;
        } catch (error) {
            console.error('Error fetching teacher type:', error);
            return null;
        }
    };

    useEffect(() => {
        const getTeacherTypes = async () => {
            const types = {};
            for (const item of teacherData) {
                if (!types[item.teacherId]) {
                    const type = await fetchTeacherType(item.teacherId);
                    // console.log('item.teacherId ', item.teacherId, ' type ', type)

                    types[item.teacherId] = type;
                }
            }
            setTeacherTypes(types);
        };

        getTeacherTypes();
    }, [teacherData]);

    const handleApproveReject = async (teacherId, attendanceId, status) => {
        try {
            const response = await fetch(
                `${apiURL1}/superadmin/teachers/${teacherId}/approveAttendance/${attendanceId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ status: status }),
                }
            );

            if (response.ok) {
                toast.success(`Attendance ${status ? 'approved' : 'rejected'} successfully!`);
                fetchTeacherData(currentPage); // Refresh data
            } else {
                toast.error('Error approving/rejecting attendance!');
            }
        } catch (error) {
            console.error('Error approving/rejecting attendance:', error);
            toast.error('Error approving/rejecting attendance!');
        }
    };

    const handleEditAttendance = async (teacherId, attendance) => {
        setSelectedTeacherId(teacherId);
        setSelectedAttendanceId(attendance._id);
        setSelectedAttendanceDate(attendance.date);

        // Parse API dates as UTC and convert to local time
        const parseAsUTCThenLocal = (dateStr) => {
            if (!dateStr) return null;
            // Force UTC parsing by appending 'Z' if missing
            const isoDateStr = dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`;
            return new Date(isoDateStr);
        };

        const checkIn = parseAsUTCThenLocal(attendance.checkIn);
        const checkOut = parseAsUTCThenLocal(attendance.checkOut);

        // Format dates/times for input fields
        setEditAttendanceData({
            checkInTime: formatTimeForInput(checkIn), // Local time
            checkOutTime: formatTimeForInput(checkOut), // Local time
            breaks: attendance.breaks || [],
        });
        // Format breaks for editing
        const formattedBreaks = attendance.breaks.map(breakItem => ({
            start: formatBreakTime(breakItem.start),
            end: formatBreakTime(breakItem.end),
        }));
        setEditBreaks(formattedBreaks);
        setEditModalOpen(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditAttendanceData({ ...editAttendanceData, [name]: value });
    };

    const updateAttendance = async () => {
        try {
            if (!selectedAttendanceDate) {
                console.error('selectedAttendanceDate is null or undefined');
                return;
            }

            const attendanceDate = new Date(selectedAttendanceDate);

            // Function to create a Date object from the selected date and time input
            const createDateTime = (time) => {
                if (!time) return null;
                const [hours, minutes] = time.split(':');
                const newDate = new Date(attendanceDate);
                newDate.setHours(parseInt(hours, 10));
                newDate.setMinutes(parseInt(minutes, 10));
                newDate.setSeconds(0);
                newDate.setMilliseconds(0);
                return newDate;
            };

            const checkInDate = createDateTime(editAttendanceData.checkInTime);
            const checkOutDate = createDateTime(editAttendanceData.checkOutTime);

            if (!checkInDate || !checkOutDate) {
                toast.error('Invalid check-in or check-out time.');
                return;
            }

            // Validate check-out is after check-in
            if (checkOutDate <= checkInDate) {
                toast.error('Check-out time must be after check-in time.');
                return;
            }

            // Convert break times to ISO format
            const formattedBreaks = editBreaks.map(breakItem => {
                const startBreakDate = createDateTime(breakItem.start);
                const endBreakDate = createDateTime(breakItem.end);

                if (!startBreakDate || !endBreakDate) {
                    toast.error('Invalid break time.');
                    return null;
                }

                return {
                    start: startBreakDate.toISOString(),
                    end: endBreakDate.toISOString(),
                };
            }).filter(breakItem => breakItem !== null); // Filter out any null break items

            if (formattedBreaks.length !== editBreaks.length) {
                return; // Stop if there was an error in break time formatting
            }

            // Convert to ISO strings (UTC)
            const checkInISO = checkInDate.toISOString();
            const checkOutISO = checkOutDate.toISOString();

            // Send the UTC dates to the API
            const response = await fetch(
                `${apiURL1}/superadmin/teachers/${selectedTeacherId}/attendance/${selectedAttendanceId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        checkIn: checkInISO,
                        checkOut: checkOutISO,
                        breaks: formattedBreaks,
                    }),
                }
            );

            if (response.ok) {
                toast.success('Attendance updated successfully!');
                fetchTeacherData(currentPage);
            } else {
                toast.error('Error updating attendance!');
            }
        } catch (error) {
            console.error('Error updating attendance:', error);
            toast.error('Error updating attendance!');
        } finally {
            setEditModalOpen(false);
        }
    };

    const capitalizeFirstLetter = (string) => {
        return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
    };

    const toggleBreakDetailsModal = () => {
        setBreakDetailsModalOpen(!breakDetailsModalOpen);
    };

    // Function to add a new break in the edit modal
    const addEditBreak = () => {
        setEditBreaks([...editBreaks, { start: '00:00', end: '00:00' }]);
    };

    // Function to remove a break in the edit modal
    const removeEditBreak = (index) => {
        const newBreaks = [...editBreaks];
        newBreaks.splice(index, 1);
        setEditBreaks(newBreaks);
    };

    // Function to update a break in the edit modal
    const updateEditBreak = (index, field, value) => {
        const newBreaks = [...editBreaks];
        newBreaks[index][field] = value;
        setEditBreaks(newBreaks);
    };

    return (
        <>
            {loading ? (
                <Spinner size="lg" className="my-5 mx-auto" />
            ) : (
                <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                        <tr>
                            {teacherColumns.map((col, index) => (
                                <th key={index}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {teacherData.length > 0 ? (
                            teacherData.map((item) => (
                                <tr key={item.teacherId + item.date}>
                                    <td>{item.name}</td>
                                    <td>{item.email}</td>
                                    <td>{formatDate(item.date)}</td>
                                    <td>
                                        {item.checkIn
                                            ? (() => {
                                                const d = new Date(item.checkIn);
                                                const hours = String(d.getHours()).padStart(2, '0');
                                                const minutes = String(d.getMinutes()).padStart(2, '0');
                                                return `${hours}:${minutes}`;
                                            })()
                                            : '-'}
                                    </td>
                                    <td>
                                        {item.checkOut
                                            ? (() => {
                                                const d = new Date(item.checkOut);
                                                const hours = String(d.getHours()).padStart(2, '0');
                                                const minutes = String(d.getMinutes()).padStart(2, '0');
                                                return `${hours}:${minutes}`;
                                            })()
                                            : '-'}
                                    </td>
                                    <td>{calculateHoursServed(item.checkIn, item.checkOut)}</td>
                                    <td>{item.totalWorkingHours}</td>
                                    <td
                                        style={{ cursor: 'pointer', color: 'blue' }}
                                        onClick={() => {
                                            setSelectedBreaks(item.breaks);
                                            toggleBreakDetailsModal();
                                        }}
                                    >
                                        {item.totalBreakTime}
                                    </td>
                                    <td>{capitalizeFirstLetter(item.dayStatus)}</td>
                                    <td>{item.isLate ? 'Yes' : 'No'}</td>

                                    {/* Actions Column - Ensuring Buttons Stay in One Row */}
                                    <td className="d-flex align-items-center gap-2">
                                        {item.status === "Present" ? (
                                            <>
                                                {(teacherTypes[item.teacherId] === "part-time" || teacherTypes[item.teacherId] === "visit") &&
                                                    !item.isApprove && (
                                                        <>
                                                            <Button color="success" size="sm" onClick={() => handleApproveReject(item.teacherId, item._id, true)}>
                                                                ✓
                                                            </Button>
                                                            <Button color="danger" size="sm" onClick={() => handleApproveReject(item.teacherId, item._id, false)}>
                                                                ✕
                                                            </Button>
                                                        </>
                                                    )}
                                                <Button color="info" size="sm" onClick={() => handleEditAttendance(item.teacherId, item)}>
                                                    <FaPencilAlt />
                                                </Button>
                                                <Button color="primary" size="sm" onClick={() => navigate(`/admin/AddTeacher/${item.teacherId}`)}>
                                                    <FaArrowRight />
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="me-2">{capitalizeFirstLetter(item.status)}</span>
                                                <Button color="primary" size="sm" onClick={() => navigate(`/admin/AddTeacher/${item.teacherId}`)}>
                                                    <FaArrowRight />
                                                </Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={teacherColumns.length} className="text-center">No staff attendance data found.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>

            )}

            {/* Edit Attendance Modal */}
            <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(!editModalOpen)} size="lg">
                <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)}>Edit Attendance</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Check-in Time</Label>
                        <Input
                            type="time"
                            name="checkInTime"
                            value={editAttendanceData.checkInTime}
                            onChange={handleEditInputChange}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Check-out Time</Label>
                        <Input
                            type="time"
                            name="checkOutTime"
                            value={editAttendanceData.checkOutTime}
                            onChange={handleEditInputChange}
                        />
                    </FormGroup>

                    {/* Edit Breaks */}
                    <FormGroup>
                        <Label>Breaks</Label>
                        {editBreaks && editBreaks.length > 0 ? (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th>Start Time</th>
                                        <th>End Time</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {editBreaks.map((breakItem, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Input
                                                    type="time"
                                                    value={breakItem.start}
                                                    onChange={(e) => updateEditBreak(index, 'start', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Input
                                                    type="time"
                                                    value={breakItem.end}
                                                    onChange={(e) => updateEditBreak(index, 'end', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => removeEditBreak(index)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p>No break details available.</p>
                        )}
                        <Button color="primary" onClick={addEditBreak}>
                            Add Break
                        </Button>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={updateAttendance}>
                        Update
                    </Button>
                    <Button color="secondary" onClick={() => setEditModalOpen(!editModalOpen)}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Break Details Modal */}
            <Modal isOpen={breakDetailsModalOpen} toggle={toggleBreakDetailsModal} size="lg">
                <ModalHeader toggle={toggleBreakDetailsModal}>Break Details</ModalHeader>
                <ModalBody>
                    {selectedBreaks && selectedBreaks.length > 0 ? (
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Start Time</th>
                                    <th>End Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedBreaks.map((breakItem) => (
                                    <tr key={breakItem._id}>
                                        <td>
                                            {(() => {
                                                const d = new Date(breakItem.start);
                                                const hours = String(d.getHours()).padStart(2, '0');
                                                const minutes = String(d.getMinutes()).padStart(2, '0');
                                                return `${hours}:${minutes}`;
                                            })()}
                                        </td>
                                        <td>
                                            {(() => {
                                                const d = new Date(breakItem.end);
                                                const hours = String(d.getHours()).padStart(2, '0');
                                                const minutes = String(d.getMinutes()).padStart(2, '0');
                                                return `${hours}:${minutes}`;
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <p>No break details available.</p>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleBreakDetailsModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            <ToastContainer />
        </>
    );
};

export default TeacherAttendance;

